import { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import reviewsData from '../../data/reviews.json';

const Testimonials: React.FC = () => {
  const controls = useAnimation();
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          controls.start({ opacity: 1, y: 0 });
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(ref);
    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, [ref, controls]);

  // Truncate review text for card display
  const truncate = (text: string, maxLen = 180) =>
    text.length <= maxLen ? text : text.slice(0, maxLen).replace(/\s+\S*$/, '') + '...';

  return (
    <div className="py-16 bg-gray-50 dark:bg-brand-dark">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-dark dark:text-brand-white text-center mb-10">
          {t('testimonials.title')}
        </h2>

        <div ref={setRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviewsData.reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 50 }}
              animate={controls}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="bg-white dark:bg-brand-charcoal rounded-2xl overflow-hidden shadow-sm"
            >
              {/* Review photo */}
              {review.media?.[0] && (
                <img
                  src={review.media[0].url}
                  alt={`${review.customerName}'s purchase`}
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                />
              )}

              {/* Content */}
              <div className="p-5 space-y-3">
                {/* Name + verified badge */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-brand-dark dark:text-brand-white">
                    {review.customerName}
                  </h3>
                  {review.verifiedPurchase && (
                    <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-500">
                      <FontAwesomeIcon icon={faCircleCheck} className="h-3" />
                      {t('testimonials.verified')}
                    </span>
                  )}
                </div>

                {/* Stars */}
                <div className="flex text-yellow-500 gap-0.5">
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <FontAwesomeIcon key={i} icon={faStar} className="h-3.5" />
                  ))}
                </div>

                {/* Review text */}
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {truncate(review.review)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
